import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useDispatch } from "react-redux";
import { CrudModal } from "../../components/_index";
import { showToast } from "../../redux/Reducer/toastReducer";
import {
  extendTicketExpiry,
  getTicketExpiryHistory,
} from "../../services/ticket.service";
import { calculateWorkingDeadline } from "../../utils/timerUtils";

const DATE_FORMAT = "DD MMM YYYY, hh:mm A";
const MAX_EXTEND_DAYS = 365;

const ExtendExpiryDialog = ({ ticket, holidays, handleClose, onExtended }) => {
  const dispatch = useDispatch();
  const [expiry, setExpiry] = useState(Number(ticket?.expiry) || 2);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isClosed = ticket?.ticketStatus === "1";
  const extendBy = Number(days) || 0;
  const deadline = (totalDays) =>
    calculateWorkingDeadline(ticket?.createdAt, totalDays, holidays).format(
      DATE_FORMAT
    );

  useEffect(() => {
    getTicketExpiryHistory(ticket._id)
      .then((res) => {
        setExpiry(res?.data?.expiry || 2);
        setHistory(res?.data?.history || []);
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [ticket._id]);

  const onSubmit = async () => {
    const nextErrors = {};
    if (extendBy < 1 || extendBy > MAX_EXTEND_DAYS) {
      nextErrors.days = `Enter the number of days (1 to ${MAX_EXTEND_DAYS}) *`;
    }
    if (!remarks.trim()) nextErrors.remarks = "Enter the reason for the extension *";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      // `changedBy` is derived server-side from the auth token, not sent here.
      const res = await extendTicketExpiry(ticket._id, {
        days: extendBy,
        remarks: remarks.trim(),
      });
      setExpiry(res.data.expiry);
      setHistory(res.data.history);
      setDays("");
      setRemarks("");
      dispatch(
        showToast({
          message: `Complete date extended to ${res.data.expiry} days`,
          type: "success",
        })
      );
      onExtended(res.data.expiry);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.data?.message || "Failed to extend the complete date",
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CrudModal
      title={`Complete Date - ${ticket?.ticketId || ""}`}
      open={true}
      handleClose={handleClose}
      maxWidth="md"
    >
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
        <Box>
          <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
            Complete Within
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{expiry} working days</Typography>
        </Box>
        <Box>
          <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
            Complete By
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{deadline(expiry)}</Typography>
        </Box>
        <Box>
          <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
            Times Extended
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{history.length}</Typography>
        </Box>
      </Box>

      {!isClosed && (
        <Box sx={{ border: "1px solid #D5D7E8", borderRadius: "8px", p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Extend Complete Date</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ width: 180 }}>
              <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                Extend By (Days)
              </Typography>
              <TextField
                placeholder="No. of days"
                value={days}
                onChange={(e) => {
                  if (/^\d{0,3}$/.test(e.target.value)) setDays(e.target.value);
                }}
                fullWidth
                size="small"
                inputProps={{ inputMode: "numeric" }}
              />
              <div className="form-error">{errors.days || null}</div>
            </Box>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                Reason for Extension
              </Typography>
              <TextField
                placeholder="Why does this ticket need more time?"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
              <div className="form-error">{errors.remarks || null}</div>
            </Box>
          </Box>
          {extendBy > 0 && extendBy <= MAX_EXTEND_DAYS && (
            <Typography sx={{ fontSize: "13px", color: "#00675D", mt: 1 }}>
              {expiry} → {expiry + extendBy} working days · New complete by:{" "}
              {deadline(expiry + extendBy)}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "20px", mt: 2 }}>
            <Button className="cancelButton" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="addButton" onClick={onSubmit} disabled={saving}>
              {saving ? "Extending..." : "Extend"}
            </Button>
          </Box>
        </Box>
      )}

      <Typography sx={{ fontWeight: 600, mb: 1 }}>Extension History</Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : history.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: "#6C7278", mb: 2 }}>
          The complete date has not been extended.
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 260, overflow: "auto", mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Changed On</TableCell>
                <TableCell>Changed By</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {moment(entry.changedAt).format(DATE_FORMAT)}
                  </TableCell>
                  <TableCell>{entry.changedByName || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {entry.from} → {entry.to}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>{entry.remarks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </CrudModal>
  );
};

export default ExtendExpiryDialog;
